import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCart } from '../contexts/CartContext';
import GlassCard from './GlassCard';
import LazyImage from './LazyImage';

const ProductCard = ({ 
  product, 
  layout = 'grid', 
  className = '',
  showAddToCart = true,
  onProductClick,
  ...props
}) => {
  const { addToCart, isInCart, getItemQuantity } = useCart();
  const [mediaIndex, setMediaIndex] = useState(0);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touchStartX = useRef(null);

  // Close on Escape
  const handleKeyUp = useCallback((e) => {
    if (e.key === 'Escape') setZoomOpen(false);
  }, []);

  useEffect(() => {
    if (zoomOpen) {
      document.addEventListener('keyup', handleKeyUp);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keyup', handleKeyUp);
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keyup', handleKeyUp);
      document.body.style.overflow = '';
    };
  }, [zoomOpen, handleKeyUp]);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const imageUrl = product.image
    ? (product.image.startsWith('http') || product.image.startsWith('/')
        ? product.image
        : `${API_BASE}/uploads/products/${product.image}`)
    : null;
  const videoUrl = product.video
    ? (product.video.startsWith('http') ? product.video : `${API_BASE}/uploads/videos/${product.video}`)
    : null;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (product.stock > 0) addToCart({ ...product, imageUrl }, 1);
  };

  const handleCardClick = () => {
    if (onProductClick) onProductClick(product);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); }
  };

  const inCart = isInCart(product.id);
  const quantity = getItemQuantity(product.id);
  const isOutOfStock = product.stock === 0;
  const isLowStock = product.stock > 0 && product.stock <= 5;
  const isMediumStock = product.stock > 5 && product.stock <= 20;

  const slides = [
    ...(imageUrl ? ['image'] : []),
    ...(videoUrl ? ['video'] : []),
  ];
  const totalSlides = slides.length;

  const goTo = (idx, e) => {
    if (e) e.stopPropagation();
    setMediaIndex(Math.max(0, Math.min(idx, totalSlides - 1)));
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) goTo(delta > 0 ? mediaIndex + 1 : mediaIndex - 1, null);
    touchStartX.current = null;
  };

  const layoutClasses = { grid: 'flex flex-col', list: 'flex flex-row' };
  const imageClasses = {
    grid: 'aspect-[3/4] w-full',
    list: 'w-40 sm:w-56 flex-shrink-0 self-stretch min-h-[140px]'
  };
  const contentClasses = { grid: 'flex-1', list: 'flex-1 min-w-0 p-4' };
  const currentSlide = slides[mediaIndex];

  return (
    <>
      <GlassCard
        className={`${layoutClasses[layout]} ${className} ${layout === 'list' ? 'p-0 overflow-hidden' : 'p-4'} ${onProductClick ? 'cursor-pointer focus:ring-2 focus:ring-glass-blue focus:outline-none' : ''}`}
        hover={!!onProductClick}
        onClick={onProductClick ? handleCardClick : undefined}
        onKeyDown={onProductClick ? handleKeyDown : undefined}
        tabIndex={onProductClick ? 0 : -1}
        role={onProductClick ? 'button' : 'article'}
        aria-label={onProductClick ? `View details for ${product.name}` : undefined}
        {...props}
      >
        {/* Media viewer */}
        <div
          className={`${imageClasses[layout]} bg-white dark:bg-gray-800 rounded-lg ${layout === 'grid' ? 'mb-4' : 'mr-0'} relative overflow-hidden select-none group`}
          onTouchStart={totalSlides > 1 ? handleTouchStart : undefined}
          onTouchEnd={totalSlides > 1 ? handleTouchEnd : undefined}
          onClick={(e) => {
            if (imageUrl && currentSlide === 'image') {
              e.stopPropagation();
              setZoomOpen(true);
            }
          }}
        >
          {/* Image slide */}
          {currentSlide === 'image' && (
            imageUrl ? (
              <>
                <LazyImage
                  src={imageUrl}
                  alt={`${product.name} - ${product.category || 'Product'} priced at KSh ${parseFloat(product.price).toFixed(2)}`}
                  className="absolute inset-0 w-full h-full"
                />
                {/* Zoom hint */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-black/50 rounded-full p-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center" role="img" aria-label="No product image available">
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs text-gray-500">No Image</span>
              </div>
            )
          )}

          {/* Video slide */}
          {currentSlide === 'video' && (
            <video
              src={videoUrl}
              controls
              className="absolute inset-0 w-full h-full object-contain bg-black"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Arrow nav + dots */}
          {totalSlides > 1 && (
            <>
              {mediaIndex > 0 && (
                <button onClick={(e) => goTo(mediaIndex - 1, e)} className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors z-10" aria-label="Previous media">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" /></svg>
                </button>
              )}
              {mediaIndex < totalSlides - 1 && (
                <button onClick={(e) => goTo(mediaIndex + 1, e)} className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors z-10" aria-label="Next media">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" /></svg>
                </button>
              )}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {slides.map((s, i) => (
                  <button key={s} onClick={(e) => goTo(i, e)} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === mediaIndex ? 'bg-white' : 'bg-white/40'}`} aria-label={s === 'video' ? 'Switch to video' : 'Switch to image'} />
                ))}
              </div>
              {currentSlide === 'video' && (
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full z-10 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" /></svg>
                  Video
                </div>
              )}
            </>
          )}

          {/* Badges */}
          {isOutOfStock && (
            <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full z-10" role="status">Out of Stock</div>
          )}
          {inCart && (
            <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full z-10" role="status">In Cart ({quantity})</div>
          )}
        </div>

        {/* Product Content */}
        <div className={contentClasses[layout]}>
          <h3 className="font-semibold text-gray-800 dark:text-white mb-2 line-clamp-2">{product.name}</h3>

          {product.category && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{product.category}</p>
          )}

          {product.description && (
            <div className="mb-2">
              <p className={`text-gray-600 dark:text-gray-300 text-sm transition-all duration-300 ${expandedDescription ? '' : 'line-clamp-3'}`}>
                {product.description}
              </p>
              {layout === 'grid' && product.description.length > 150 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setExpandedDescription((prev) => !prev); }}
                  className="text-xs font-semibold text-glass-blue hover:text-glass-blue-dark mt-1 focus:outline-none focus:underline"
                  aria-expanded={expandedDescription}
                >
                  {expandedDescription ? '↑ Show less' : '↓ Read more'}
                </button>
              )}
            </div>
          )}

          <div className="mb-3" role="status">
            {isOutOfStock ? (
              <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">Out of Stock</span>
            ) : isLowStock ? (
              <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">Only {product.stock} left</span>
            ) : isMediumStock ? (
              <span className="inline-block bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium px-2 py-1 rounded-full">{product.stock} in stock</span>
            ) : (
              <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium px-2 py-1 rounded-full">In Stock ({product.stock})</span>
            )}
          </div>

          <div className={`flex items-center mt-auto ${layout === 'list' ? 'flex-col items-start gap-2 sm:flex-row sm:justify-between sm:items-center' : 'justify-between'}`}>
            <div className="flex flex-col" role="group" aria-label="Product pricing">
              <span className="text-lg font-bold text-gray-800 dark:text-white">
                KSh {parseFloat(product.price).toFixed(2)}
              </span>
              {product.originalPrice && parseFloat(product.originalPrice) > parseFloat(product.price) && (
                <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                  KSh {parseFloat(product.originalPrice).toFixed(2)}
                </span>
              )}
            </div>

            {showAddToCart && (
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className={`glass-button-primary min-h-[44px] min-w-[44px] ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 transform transition-transform focus:ring-2 focus:ring-glass-blue focus:outline-none'}`}
                aria-label={isOutOfStock ? `${product.name} is out of stock` : inCart ? `Add another ${product.name} to cart` : `Add ${product.name} to cart`}
              >
                {isOutOfStock ? 'Out of Stock' : inCart ? 'Add More' : 'Add to Cart'}
              </button>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Zoom overlay */}
      {zoomOpen && imageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-fadeIn"
          onClick={() => setZoomOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Zoomed view of ${product.name}`}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors z-10"
            onClick={() => setZoomOpen(false)}
            aria-label="Close zoom"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Zoomed image — scales up from nothing for natural feel */}
          <div
            className="relative bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-2xl"
            style={{
              width: 'min(90vw, 600px)',
              height: 'min(90vh, 600px)',
              animation: 'zoomIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageUrl}
              alt={product.name}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </div>

          <p className="absolute bottom-6 left-0 right-0 text-center text-white text-sm font-medium px-4 drop-shadow">
            {product.name}
          </p>
        </div>
      )}
    </>
  );
};

export default ProductCard;
